import React from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { graphql, compose, withApollo } from 'react-apollo';
import { connect } from 'react-redux';
import gql from 'graphql-tag';
import { FormattedMessage, injectIntl } from 'react-intl';
import cx from 'classnames';
import { Crown } from 'lucide-react';
import s from './PremiumListing.css';
import l from '../../Skeleton/Skeleton.css'
//Components
import SeeAll from '../SeeAll';
import HomeSlider from '../HomeSlider';
// Skeleton Loader
import Shimmer from "../../Skeleton/Shimmer";
import HomeSliderSkleton from "../../Skeleton/HomeSliderSkleton";
// Locale
import messages from '../../../locale/messages';
// Redux
import { getActiveCategory } from '../../../reducers/category';

// Query for premium listings only
const GET_PREMIUM_LISTINGS = gql`
  query getRecommend($tier: String, $categoryType: String) {
    getRecommend(tier: $tier, categoryType: $categoryType) {
      id
      title
      personCapacity
      beds
      bookingType
      coverPhoto
      reviewsCount
      reviewsStarRating
      transmission
      userId
      isFeatured
      featuredTier
      categoryType
      listPhotos {
        id
        name
        type
        status
      }
      listingData {
        basePrice
        currency
        hourlyPrice
        bookingMode
      }
      settingsData {
        listsettings {
          id
          itemName
        }
      }
      wishListStatus
      isListOwner
    }
  }
`;

// Mutation for tracking impressions
const TRACK_IMPRESSIONS = gql`
  mutation trackFeaturedImpression($listIds: [Int]!) {
    trackFeaturedImpression(listIds: $listIds) {
      status
      tracked
    }
  }
`;

class PremiumListing extends React.Component {

  static propTypes = {
    getPremiumData: PropTypes.shape({
      loading: PropTypes.bool,
      getRecommend: PropTypes.array
    })
  };

  static defaultProps = {
    getPremiumData: {
      loading: true
    }
  };

  // Track which listings we've already tracked in this session
  trackedListings = new Set();

  componentDidUpdate(prevProps) {
    const { getPremiumData, client, activeCategory } = this.props;

    // Refetch when activeCategory changes
    if (prevProps.activeCategory !== activeCategory && getPremiumData && getPremiumData.refetch) {
      getPremiumData.refetch({
        tier: 'premium',
        categoryType: activeCategory || 'boat'
      });
      return;
    }

    // Only track when data first loads or changes
    if (
      !getPremiumData?.loading &&
      getPremiumData?.getRecommend?.length > 0 &&
      (prevProps.getPremiumData?.loading ||
        prevProps.getPremiumData?.getRecommend?.length !== getPremiumData.getRecommend.length)
    ) {
      this.trackImpressions(getPremiumData.getRecommend);
    }
  }

  trackImpressions = async (listings) => {
    const { client } = this.props;

    try {
      // Filter only featured listings that haven't been tracked this session
      const featuredListIds = listings
        .filter(l => l.isFeatured && !this.trackedListings.has(l.id))
        .map(l => l.id);

      if (featuredListIds.length === 0) return;

      // Mark as tracked immediately to prevent duplicate calls
      featuredListIds.forEach(id => this.trackedListings.add(id));

      // Call mutation (fire and forget - don't block UI)
      await client.mutate({
        mutation: TRACK_IMPRESSIONS,
        variables: { listIds: featuredListIds }
      });

    } catch (error) {
      // Silent fail - don't break the UI for analytics
      console.debug('[PremiumListing] Impression tracking error:', error.message);
    }
  };

  render() {
    const { getPremiumData, skeletonArray, activeCategory, intl: { formatMessage } } = this.props;
    const hasData = getPremiumData?.getRecommend?.length > 0;

    // Dynamic title based on category
    const titleMessage = activeCategory === 'posada'
      ? messages.premiumPosadas
      : activeCategory === 'experience'
        ? messages.premiumExperiences
        : messages.premiumBoats;

    // Don't render anything if no premium listings
    if (!getPremiumData?.loading && !hasData) {
      return null;
    }

    return (
      <>
        {getPremiumData && getPremiumData.loading ? (
          <div className={s.pageContainer}>
            <div className={cx(s.container, 'containerLoaderRTL')}>
              <div className={cx(l.popularSkeletonTitle, 'bgBlackTwo')}>
                <Shimmer />
              </div>
              <div className={s.displayGrid}>
                {skeletonArray.map((n) => (
                  <HomeSliderSkleton key={n} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            {hasData && (
              <div className={s.premiumBg}>
                <div className={s.container}>
                  <div className={s.sectionHeader}>
                    <h2 className={s.containerTitle}>
                      <Crown className={s.crownIcon} size={28} />
                      <span className={s.titleText}>
                        <FormattedMessage {...titleMessage} />
                      </span>
                    </h2>
                    <SeeAll />
                  </div>
                  <HomeSlider data={getPremiumData.getRecommend} />
                </div>
              </div>
            )}
          </>
        )}
      </>
    )
  }
}

const mapState = (state) => ({
  activeCategory: getActiveCategory(state)
});

export default compose(
  injectIntl,
  withStyles(s, l),
  connect(mapState),
  withApollo,
  graphql(GET_PREMIUM_LISTINGS, {
    name: 'getPremiumData',
    options: (props) => ({
      variables: { tier: 'premium', categoryType: props.activeCategory || 'boat' },
      ssr: false,
      fetchPolicy: 'network-only'
    })
  })
)(PremiumListing);
